import { memo, useCallback } from 'react';

import { BasicTooltip, Label, Separator, Text, TextField } from '@/components/core';
import { TextArea } from '@/components/core';
import { findField, FormatterValidatorHeader } from '@/components/ui';
import { useDebounceCallback, useFormatterMutation } from '@/hooks';
import { useModelConfigStore } from '@/store';

import { FormatterTest } from './FormatterTest';

export type RegexFormatterProps = {
  fieldId: string;
  formatterId: string;
};

const IRegexFormatter = ({ fieldId, formatterId }: RegexFormatterProps) => {
  const field = useModelConfigStore((state) => findField(state.fields, fieldId));
  const formatter = field?.formatters?.find((f) => f.id === formatterId);

  const mutate = useFormatterMutation({ fieldId, formatterId, parentId: field?.parentId });

  const onSetMatchPattern = useDebounceCallback(
    useCallback((matchPattern: string) => mutate((f) => (f.config.matchPattern = matchPattern || undefined)), [mutate]),
    500
  );

  const onSetReplacePattern = useDebounceCallback(
    useCallback((replacePattern: string) => mutate((f) => (f.config.replacePattern = replacePattern || undefined)), [mutate]),
    500
  );

  const onSetMatchFlags = useDebounceCallback(
    useCallback((matchFlags: string) => mutate((f) => (f.config.matchFlags = matchFlags || undefined)), [mutate]),
    500
  );

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      <FormatterValidatorHeader formatter={formatter} />

      <Separator orientation="horizontal" />
      <div className="flex grow flex-col gap-4">
        <TextField onChange={onSetMatchPattern} defaultValue={formatter?.config.matchPattern?.toString()}>
          <div className="gap-xs flex">
            <Label isRequired>Match pattern</Label>
            <BasicTooltip>
              <Text slot="description">
                JS RegExp pattern. See{' '}
                <a
                  className="underline"
                  href="https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_expressions/Cheatsheet"
                  target="_blank"
                >
                  regex cheatsheet
                </a>{' '}
                for more details and examples.
              </Text>
            </BasicTooltip>
          </div>
          <TextArea placeholder="Example: ^(.*) Corp$" />
        </TextField>

        <TextField onChange={onSetReplacePattern} defaultValue={formatter?.config.replacePattern?.toString()}>
          <div className="gap-xs flex">
            <Label>Replace pattern</Label>
            <BasicTooltip>
              <Text slot="description">
                JS string replace with RegExp pattern support. See{' '}
                <a
                  className="underline"
                  href="https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/replace"
                  target="_blank"
                >
                  string replace documentation
                </a>{' '}
                for more details and examples.
              </Text>
            </BasicTooltip>
          </div>
          <TextArea placeholder="Example: $1 Corporation" />
        </TextField>

        <TextField onChange={onSetMatchFlags} defaultValue={formatter?.config.matchFlags?.toString()}>
          <div className="gap-xs flex">
            <Label>Match flags</Label>
            <BasicTooltip>
              <Text slot="description">
                JS RegExp match flags. Multiple flags can be combined by providing a single string containing all flags
                desired, eg. "gs". See{' '}
                <a
                  className="underline"
                  href="https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_expressions#advanced_searching_with_flags"
                  target="_blank"
                >
                  regex flags documentation
                </a>{' '}
                for more details and examples.
              </Text>
            </BasicTooltip>
          </div>
          <TextArea placeholder="Example: gs" />
        </TextField>
      </div>

      <FormatterTest fieldId={fieldId} formatterId={formatterId} />
    </div>
  );
};

export const RegexFormatter = memo(IRegexFormatter) as typeof IRegexFormatter;
